import { useState, useCallback, useEffect, useRef } from 'react';
import useApi from '../../Api';
import { useAuth } from '../../AuthContext';

const usePagination = (
  endpoint,
  initialOrderBy = 'newest',
  cacheRef,
  originKey,
  type = null,
  userSpecific = false
) => {
  const [items, setItems] = useState([]);
  const [orderBy, setOrderBy] = useState(initialOrderBy);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const hasFetchedRef = useRef(false);

  const apiClient = useApi();
  const { token } = useAuth();

  const fetchItems = useCallback(
    async (cursor = null, append = false, search = '', overrideOrderBy = null) => {
      if (loadingMore || isRefreshing) return;
      if (append) {
        setLoadingMore(true);
      } else if (items.length === 0) {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }
      setError(null);

      const usedOrderBy = overrideOrderBy ?? orderBy;
      const trimmedSearch = (search || '').trim();
      const cacheKey = usedOrderBy + '|' + trimmedSearch;

      if (!append && cacheRef.current[originKey] && cacheRef.current[originKey][cacheKey]) {
        const cached = cacheRef.current[originKey][cacheKey];
        setItems(cached.items);
        setNextCursor(cached.nextCursor);
        if (append) {
          setLoadingMore(false);
        } else if (items.length === 0) {
          setLoading(false);
        } else {
          setIsRefreshing(false);
        }
        return { items: cached.items, nextCursor: cached.nextCursor };
      }

      try {
        const params = new URLSearchParams({
          order_by: usedOrderBy,
          limit: 10
        });
        if (originKey && originKey !== 'general') {
          params.append('origin_id', originKey);
        }
        if (type) {
          params.append('type', type);
        }
        if (cursor) {
          params.append('cursor', cursor);
        }
        if (trimmedSearch.length > 0) {
          params.append('search', trimmedSearch);
        }

        const response = await apiClient.get(`${endpoint}?${params.toString()}`, {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        });

        const { items: newItems, next_cursor } = response.data;
        if (append) {
          setItems((prevItems) => [...prevItems, ...newItems]);
        } else {
          setItems(newItems);
        }
        setNextCursor(next_cursor);

        if (!cacheRef.current[originKey]) {
          cacheRef.current[originKey] = {};
        }
        cacheRef.current[originKey][cacheKey] = {
          items: append ? [...items, ...newItems] : newItems,
          nextCursor: next_cursor,
        };
        return { items: newItems, nextCursor: next_cursor };
      } catch (err) {
        if (err.response) {
          if (err.response.status === 401) {
            setError('Please Sign In or Sign Up.');
          } else if (err.response.status === 403) {
            setError('Please Verify Your Account.');
          } else if (err.response.status === 404) {
            setError('not_found');
          } else {
            setError('Failed to Load Data');
          }
        } else {
          setError('Failed to Load Data');
        }
        return null;
      } finally {
        if (append) {
          setLoadingMore(false);
        } else if (items.length === 0) {
          setLoading(false);
        } else {
          setIsRefreshing(false);
        }
      }
    },
    [apiClient, orderBy, token, loadingMore, isRefreshing, items, endpoint, cacheRef, originKey, type]
  );

  const handleOrderChange = useCallback((newOrderBy) => {
    setOrderBy(newOrderBy);
    setNextCursor(null);
    setItems([]);
  }, []);

  const handleLoadMore = useCallback(async () => {
    if (nextCursor) {
      await fetchItems(nextCursor, true);
    }
  }, [fetchItems, nextCursor]);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchItems();
      return;
    }
  }, [fetchItems]);

  return {
    items,
    orderBy,
    loading,
    loadingMore,
    isRefreshing,
    error,
    nextCursor,
    fetchItems,
    handleOrderChange,
    handleLoadMore,
  };
};

export default usePagination;
